
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, XCircle, MessageSquare } from 'lucide-react';
import { isSystemBotTokenConfigured } from '@/services/telegram/botTokenService';

interface TelegramConnectionStatusProps {
  isConnected?: boolean;
}

export function TelegramConnectionStatus({ isConnected = false }: TelegramConnectionStatusProps) {
  const [isBotConfigured, setIsBotConfigured] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const checkBotToken = async () => {
      setIsLoading(true);
      try {
        const configured = await isSystemBotTokenConfigured();
        setIsBotConfigured(configured);
      } catch (error) {
        console.error('Error checking bot configuration:', error);
        setIsBotConfigured(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkBotToken();
  }, []);
  
  const getStatusDisplay = () => {
    if (isLoading) {
      return {
        icon: <MessageSquare className="h-8 w-8 text-muted-foreground animate-pulse" />,
        title: "Checking...",
        description: "Verifying bot configuration",
        color: "bg-muted/10 text-muted-foreground"
      };
    }
    
    if (!isBotConfigured) {
      return {
        icon: <XCircle className="h-8 w-8 text-destructive" />,
        title: "Not Configured",
        description: "System Telegram bot not configured",
        color: "bg-destructive/10 text-destructive"
      };
    }
    
    if (!isConnected) {
      return {
        icon: <AlertTriangle className="h-8 w-8 text-warning" />,
        title: "Ready to Connect",
        description: "Bot configured, account not connected",
        color: "bg-warning/10 text-warning"
      };
    }
    
    return {
      icon: <CheckCircle className="h-8 w-8 text-success" />,
      title: "Connected",
      description: "Your Telegram account is connected",
      color: "bg-success/10 text-success"
    };
  };
  
  const status = getStatusDisplay();
  
  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg ${status.color}`}>
      {status.icon}
      <div>
        <h3 className="font-medium text-lg">{status.title}</h3>
        <p>{status.description}</p>
        {!isBotConfigured && (
          <p className="text-xs mt-1">
            Contact your administrator to configure the bot token
          </p>
        )}
      </div>
    </div>
  );
}
